import "reflect-metadata";

import { getCurrentParticipantSelector } from "@/features/participants/api/get-current-participant";
import { SliceCreator } from "@/features/vidyo-call/types/vidyo-common";
import {
  BreakoutSessionClosedPayload,
  BreakoutSessionsCreatedPayload,
  BreakoutSessionsDetailsPayload,
  BreakoutSessionsUpdatedPayload,
} from "@classview/shared-types";

import { ParticipantWithSessionId } from "../types/participant-with-session-id";

export interface BreakoutSessionState {
  breakoutSession: {
    participants: ParticipantWithSessionId[];
    /**
     * If not null, the session is a breakout session
     */
    parentSession: {
      id: string;
      roomKey: string;
    } | null;
    /**
     * If true, user will be forced to join to breakout session
     */
    shouldJoinToBreakoutSession: boolean;
    /**
     * The breakout room key assigned to the user
     */
    assignedBreakoutRoomKey: string | null;
    /**
     * Breakout session is closed, user should return to parent session
     */
    shouldReturnToParentSession: boolean;
    /**
     * Reason for returning to parent session
     */
    redirectReason:
      | "breakout-session-closed"
      | "breakout-session-timeout"
      | "kick-from-breakout-session"
      | "moved-to-breakout-session"
      | null;
    startBreakoutSession: (payload: BreakoutSessionsCreatedPayload) => void;
    closeBreakoutSession: (payload: BreakoutSessionClosedPayload) => void;
    updateBreakoutSessionParticipants: (
      payload: BreakoutSessionsDetailsPayload,
    ) => void;
    removeParticipant: (participantId: string) => void;
    moveParticipantToBreakoutSession: (
      payload: BreakoutSessionsUpdatedPayload,
    ) => void;
  };
}

export const createBreakoutSessionSlice: SliceCreator<
  keyof BreakoutSessionState
> = (set, get) => ({
  breakoutSession: {
    participants: [],
    /**
     * Set in call-settings-slice.tsx setSession fn
     */
    parentSession: null,
    /**
     * Reset in call-settings-slice.tsx setSession fn
     */
    shouldJoinToBreakoutSession: false,
    /**
     * Reset in call-settings-slice.tsx setSession fn
     */
    assignedBreakoutRoomKey: null,
    shouldReturnToParentSession: false,
    redirectReason: null,
    startBreakoutSession: (payload: BreakoutSessionsCreatedPayload) => {
      const currentParticipant = getCurrentParticipantSelector(get());

      if (!currentParticipant) {
        console.error("[breakout-session-slice] currentParticipant not found");
        return;
      }

      const breakoutRoomKey = payload.participants.find(
        (p) => p.participantId === currentParticipant.participantId,
      )?.breakoutRoomKey;

      if (!breakoutRoomKey) {
        console.log(
          "[breakout-session-slice] breakoutRoomKey not found for current participant",
        );
        return;
      }

      set(
        (state) => {
          state.breakoutSession.shouldJoinToBreakoutSession = true;
          state.breakoutSession.assignedBreakoutRoomKey = breakoutRoomKey;
        },
        undefined,
        "breakoutSession/startBreakoutSession",
      );
    },
    closeBreakoutSession: (payload: BreakoutSessionClosedPayload) => {
      /**
       * Only return to parent session if the closed breakout session is the
       * same as the current breakout session
       */
      const currentParticipant = getCurrentParticipantSelector(get());
      const isCurrentParticipant =
        currentParticipant?.participantId === payload.participantId;
      const isKickedFromBreakoutSession =
        payload.reason === "kick-from-breakout-session";
      const parentSession = get().breakoutSession.parentSession;

      if (!parentSession) {
        console.error(
          "You are trying to close breakout session but this is not a breakout session!",
        );
        return;
      }

      /**
       * Kicked action is affects only the passed payload.participantId
       * Other actions are affecting all participants in the breakout session
       */
      if (
        (isKickedFromBreakoutSession && isCurrentParticipant) ||
        (payload.sessionId === get().sessionId && !isKickedFromBreakoutSession)
      ) {
        set(
          (state) => {
            state.breakoutSession.shouldReturnToParentSession = true;
            state.breakoutSession.redirectReason = payload.reason;
          },
          undefined,
          "breakoutSession/closeBreakoutSession",
        );
      }
    },
    updateBreakoutSessionParticipants: (
      payload: BreakoutSessionsDetailsPayload,
    ) => {
      set(
        (state) => {
          state.breakoutSession.participants = [
            // Keep participants from other sessions
            ...state.breakoutSession.participants.filter(
              (p) => p.sessionId !== payload.sessionId,
            ),
            // Add new participants from this session
            ...payload.participants.map((p) => ({
              participantId: p.participantID,
              name: p.displayName,
              sessionId: payload.sessionId,
              entityId: p.entityId,
            })),
          ];
        },
        undefined,
        "breakoutSession/updateBreakoutSessionParticipants",
      );
    },
    removeParticipant: (participantId: string) => {
      set(
        (state) => {
          state.breakoutSession.participants =
            state.breakoutSession.participants.filter(
              (p) => p.participantId !== participantId,
            );
        },
        undefined,
        "breakoutSession/removeParticipant",
      );
    },
    moveParticipantToBreakoutSession: (
      payload: BreakoutSessionsUpdatedPayload,
    ) => {
      const currentParticipant = getCurrentParticipantSelector(get());

      if (!currentParticipant) {
        console.error("[breakout-session-slice] currentParticipant not found");
        return;
      }

      const parentSession = get().breakoutSession.parentSession;

      payload.participants.forEach((participant) => {
        // action needed only if the participant is the current participant
        if (participant.participantId === currentParticipant.participantId) {
          // we want to redirect the user from breakout session to the main call
          if (parentSession?.roomKey === participant.breakoutRoomKey) {
            set(
              (state) => {
                state.breakoutSession.shouldReturnToParentSession = true;
                state.breakoutSession.redirectReason =
                  "kick-from-breakout-session";
              },
              undefined,
              "breakoutSession/redirectToMainCall",
            );
          } else {
            // we want to redirect the user to a breakout session
            set(
              (state) => {
                state.breakoutSession.shouldJoinToBreakoutSession = true;
                state.breakoutSession.assignedBreakoutRoomKey =
                  participant.breakoutRoomKey;
                state.breakoutSession.redirectReason =
                  "moved-to-breakout-session";
              },
              undefined,
              "breakoutSession/moveParticipantToBreakoutSession",
            );
          }
        }
      });
    },
  },
});
